// RNAInnerReportComponent.js
import React from 'react';
import styles from './RNAInnerReportComponent.module.css';
// ★ 지정해주신 로고 이미지 경로
import converLogo from '../../img/gradiantbioconvergence/gradiantbio_logo.png';

function RNAInnerReportComponent({ data }) {
    if (!data) return null;

    const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

    // =================================================================
    // ★ 디자인 통일성을 위한 공통 인라인 스타일 정의
    // =================================================================
    const s_paraTitle = { fontSize: '16px', color: '#111827', fontWeight: 'bold', margin: '0 0 8px 0' };
    const s_secTitle = { fontSize: '16px', marginTop: '20px', marginBottom: '10px' };
    const s_subTitle = { fontSize: '13px', color: '#374151', fontWeight: 'bold', margin: '0 0 6px 0' };
    const s_desc = { fontSize: '12px', lineHeight: '1.6', margin: '0 0 15px 0' };
    const s_table = { fontSize: '10.5px', marginBottom: '20px' };

    // 표지(Cover Page) 정보 테이블 전용 스타일
    const s_coverTh = { textAlign: 'left', padding: '4px 24px 4px 0', border: 'none', background: 'none', fontWeight: 'bold', color: '#374151', fontSize: '12px' };
    const s_coverTd = { textAlign: 'left', padding: '4px 0', border: 'none', background: 'none', color: '#4b5563', fontSize: '12px' };

    // 컬럼 디스크립션 텍스트가 길어 화면을 벗어나는 것을 방지하기 위한 공통 표 스타일
    const descTableStyle = { ...s_table, tableLayout: 'fixed', width: '100%' };
    const descThStyle = { padding: '6px' };
    const descColTdStyle = { textAlign: 'center', padding: '6px', wordWrap: 'break-word', wordBreak: 'keep-all', whiteSpace: 'normal' };
    const descTextTdStyle = { textAlign: 'left', padding: '6px', wordWrap: 'break-word', whiteSpace: 'normal' };

    // 배열을 지정된 크기(2개)씩 쪼개는 유틸리티 함수
    const chunkArray = (arr, size) => {
        const result = [];
        for (let i = 0; i < arr.length; i += size) {
            result.push(arr.slice(i, i + size));
        }
        return result;
    };

    // 데이터 안전 추출
    const fusionImages = data.fusionDrawImgs || [];
    const chunkedImages = chunkArray(fusionImages, 2);

    // QC, FastQC 이미지, Gene Expression Profile
    const qcDataRaw = data.qcDataRaw || [];
    const fastqcImagesRaw = data.fastqcImagesRaw || {};
    const qcDataTrim = data.qcDataTrim || [];
    const fastqcImagesTrim = data.fastqcImagesTrim || {};
    const geneExpressionProfile = data.geneExpressionProfile || [];

    // ★ 파일명 파싱 유틸리티 함수 (언더바 숫자까지만 자르고 Forward/Reverse 판별)
    const getFileInfo = (rawFileName) => {
        const match = rawFileName.match(/(.*_[12])/);
        const fileName = match ? match[1] : rawFileName.split('.')[0];

        let readType = '-';
        if (fileName.endsWith('_1')) readType = 'Forward';
        else if (fileName.endsWith('_2')) readType = 'Reverse';

        return { fileName, readType };
    };

    // =================================================================
    // ★ 동적 섹션(목차) 및 페이지 번호 계산 로직
    // =================================================================
    let currentSec = 1;
    const secSummary = currentSec++;
    const secQc = currentSec++;      // 2. QC (Raw와 Trim을 하나의 번호로 통합)
    const secGeneExp = currentSec++; // 3. Gene expression profile
    const secFusion = currentSec++;  // 4. Fusion Analysis
    const secDiagram = currentSec++; // 5. Gene Fusion Diagrams

    let currentPage = 1;
    const pageNum_Cover = currentPage++;
    const pageNum_TOC = currentPage++;
    const pageNum_Mapping = currentPage++;

    const pageNum_QC_Raw = currentPage++;
    const pageNum_FastQC_Raw = currentPage++;

    const pageNum_QC_Trim = currentPage++;
    const pageNum_FastQC_Trim = currentPage++;

    const pageNum_GeneExp = currentPage++;     // Gene expression profile 페이지
    const pageNum_Fusion = currentPage++;

    // 전체 페이지 수
    const totalPages = (currentPage - 1) + chunkedImages.length;

    // =================================================================
    // ★ 스크롤 이동 핸들러
    // =================================================================
    const scrollToSec = (e, id) => {
        e.preventDefault();
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
    };

    // =================================================================
    // ★ 다운로드 핸들러
    // =================================================================

    // 1. Gene Expression 파일 다운로드 핸들러
    const handleDownloadExpression = async () => {
        try {
            // 샘플명의 세 번째 문자를 기반으로 Lung(0) 또는 Colon(2) 판별
            let tabName = 'RNA';
            if (data.sampleName && data.sampleName.charAt(2) === '0') tabName = 'Lung';
            else if (data.sampleName && data.sampleName.charAt(2) === '2') tabName = 'Colon';

            const response = await fetch('/api/rna-expression/direct-fetch', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ samples: [data.sampleName], activeTab: tabName })
            });

            if (!response.ok) {
                throw new Error(`데이터 다운로드에 실패했습니다. (Status: ${response.status})`);
            }

            const blob = await response.blob();
            if (blob.size === 0) {
                throw new Error('파일이 비어 있습니다.');
            }

            const currentDate = new Date().toISOString().slice(0, 10).replace(/-/g, '');
            const fileName = `${tabName}_gene_expression_${currentDate}.xlsx`;

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            console.error('Error downloading RNA expression data:', error);
            alert(`파일 다운로드 중 오류가 발생했습니다: ${error.message}`);
        }
    };

    // 2. Fusion 파일 다운로드 핸들러
    const handleDownloadFusion = async () => {
        try {
            const response = await fetch('/api/download', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ type: 'RNA', files: [data.sampleName], activeTab: 'RNA' })
            });

            if (!response.ok) {
                throw new Error(`서버 오류가 발생했습니다. (Status: ${response.status})`);
            }

            const blob = await response.blob();
            if (blob.size === 0) {
                throw new Error('압축된 파일이 비어 있습니다.');
            }

            const currentDate = new Date().toISOString().slice(0, 10).replace(/-/g, '');
            const fileName = `RNA_gene_fusion_${currentDate}.zip`;

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            console.error('Error downloading RNA fusion files:', error);
            alert(`파일 다운로드 중 오류가 발생했습니다: ${error.message}`);
        }
    };

    // =================================================================
    // ★ 공통 렌더링 함수들
    // =================================================================
    const renderTopLogo = (isCover = false) => (
        <div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%', marginBottom: isCover ? '0' : '10px' }}>
            <img
                src={converLogo}
                alt="Gradiant Bioconvergence Logo"
                style={{ height: isCover ? '45px' : '25px', objectFit: 'contain' }}
            />
        </div>
    );

    const renderPageHeader = (title) => (
        <div className={styles.pageHeader} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h2 style={{ margin: 0 }}>{title}</h2>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                <img
                    src={converLogo}
                    alt="Gradiant Bioconvergence Logo"
                    style={{ height: '25px', objectFit: 'contain', marginBottom: '4px' }}
                />
                <div className={styles.sampleId} style={{ margin: 0, lineHeight: '1' }}>
                    {data.sampleName}
                </div>
            </div>
        </div>
    );

    const renderFooter = (pageNum) => (
        <div className={styles.pageFooter} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', borderTop: '1px solid #e5e7eb', paddingTop: '15px', marginTop: 'auto' }}>
            <div style={{ textAlign: 'left' }}>
                <p style={{ margin: 0, fontSize: '10px', color: '#6b7280' }}>For Research Use Only. Not intended for diagnostic procedures.</p>
                <p style={{ margin: 0, fontSize: '10px', color: '#6b7280' }}>© Gradiant Bioconvergence. All Rights Reserved.</p>
            </div>
            <div style={{ textAlign: 'right' }}>
                <p style={{ margin: '0 0 6px 0', fontSize: '11px', fontWeight: 'bold', color: '#ef4444' }}>STRICTLY CONFIDENTIAL</p>
                <p style={{ margin: 0, fontSize: '11px', fontWeight: 'bold', color: '#374151' }}>Page {pageNum} / {totalPages}</p>
            </div>
        </div>
    );

    return (
        <div className={styles.reportWrapper}>

            {/* --- Page 1: 표지 --- */}
            <div className={`pdf-page ${styles.a4Page}`} style={{ display: 'flex', flexDirection: 'column' }}>
                {renderTopLogo(true)}

                <div className={styles.titleSection} style={{ marginTop: '120px', flex: 1 }}>
                    <div className={styles.brandLine}></div>
                    <h1>NGS RNA Report</h1>
                    <p className={styles.subtitle}>Next-Generation Sequencing Data Summary</p>

                    <div className={styles.infoBoxCover}>
                        <table className={styles.coverInfoTable}>
                            <tbody>
                                <tr>
                                    <th style={s_coverTh}>Sample Name</th>
                                    <td style={s_coverTd}>{data.sampleName}</td>
                                </tr>
                                <tr>
                                    <th style={s_coverTh}>Date</th>
                                    <td style={s_coverTd}>{today}</td>
                                </tr>
                                <tr>
                                    <th style={s_coverTh}>Application</th>
                                    <td style={s_coverTd}>mRNA sequencing</td>
                                </tr>
                                <tr>
                                    <th style={s_coverTh}>Type of Read</th>
                                    <td style={s_coverTd}>Paired-end</td>
                                </tr>
                                <tr>
                                    <th style={s_coverTh}>Read length</th>
                                    <td style={s_coverTd}>101bp</td>
                                </tr>
                                <tr>
                                    <th style={s_coverTh}>Library Kit</th>
                                    <td style={s_coverTd}>Illumina TruSeq RNA library</td>
                                </tr>
                                <tr>
                                    <th style={s_coverTh}>Type of Sequencer</th>
                                    <td style={s_coverTd}>Novaseq</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className={styles.footerSection} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <div className={styles.footerNotice} style={{ margin: '0 0 6px 0' }}>For Research Use Only</div>
                    <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#ef4444', marginBottom: '8px' }}>STRICTLY CONFIDENTIAL</div>
                    <div style={{ fontSize: '11px', color: '#6b7280' }}>Page {pageNum_Cover} / {totalPages}</div>
                </div>
            </div>

            {/* --- Page 2: 목차 (Table of Contents) --- */}
            <div className={`pdf-page ${styles.a4Page}`} id="section-toc">
                {renderPageHeader('Table of Contents')}

                <div style={{ marginTop: '20px', padding: '0 20px' }}>
                    <ul style={{ listStyle: 'none', padding: 0, fontSize: '15px', lineHeight: '2.5', fontWeight: 'bold' }}>
                        <li><span onClick={(e) => scrollToSec(e, 'sec-summary')} style={{ cursor: 'pointer', color: '#2563eb' }}>{secSummary}. Analysis Summary</span></li>
                        <li><span onClick={(e) => scrollToSec(e, 'sec-qc-raw')} style={{ cursor: 'pointer', color: '#2563eb' }}>{secQc}. Quality Control</span></li>
                        <li><span onClick={(e) => scrollToSec(e, 'sec-gene-exp')} style={{ cursor: 'pointer', color: '#2563eb' }}>{secGeneExp}. Gene Expression</span></li>
                        <li><span onClick={(e) => scrollToSec(e, 'sec-fusion')} style={{ cursor: 'pointer', color: '#2563eb' }}>{secFusion}. Gene Fusion</span></li>
                        <li><span onClick={(e) => scrollToSec(e, 'sec-diagram')} style={{ cursor: 'pointer', color: '#2563eb' }}>{secDiagram}. Gene Fusion Diagrams</span></li>
                    </ul>
                </div>

                {renderFooter(pageNum_TOC)}
            </div>

            {/* --- Page 3: Mapping Summary (RNA) --- */}
            <div className={`pdf-page ${styles.a4Page}`} id="sec-summary">
                {renderPageHeader(`${secSummary}. Analysis Summary`)}

                <p className={styles.description} style={{ ...s_desc, marginBottom: '20px' }}>
                    The key mapping results and overall transcriptome expression metrics of the RNA NGS data analysis. The table displays the Total Read, representing the total volume of generated data, the Mapped Read, indicating the number of reads successfully aligned to the reference genome, and its corresponding percentage (Mapping Rate, %). Additionally, it provides the total number of genes with detected expression in the target sample (Expressed Gene), along with the number of fusion genes identified through the analysis (Fusion Gene).
                </p>

                <h3 className={styles.sectionTitle} style={{ ...s_secTitle, marginTop: '0', marginBottom: '8px' }}>Mapping Summary</h3>
                <table className={styles.dataTable} style={s_table}>
                    <thead>
                        <tr>
                            <th>Sample Name</th>
                            <th>Total Read</th>
                            <th>Mapped Read</th>
                            <th>Mapping Rate (%)</th>
                            <th>Expressed Gene</th>
                            <th>Fusion Gene</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>{data.sampleName}</td>
                            <td>{data.mappingSummary?.totalRead ? Number(data.mappingSummary.totalRead).toLocaleString() : '-'}</td>
                            <td>{data.mappingSummary?.mappedRead ? Number(data.mappingSummary.mappedRead).toLocaleString() : '-'}</td>
                            <td>{data.mappingSummary?.percent || '-'}</td>
                            <td>{data.mappingSummary?.expressedGene ? Number(data.mappingSummary.expressedGene).toLocaleString() : '-'}</td>
                            <td>{data.mappingSummary?.fusionGene ? Number(data.mappingSummary.fusionGene).toLocaleString() : '-'}</td>
                        </tr>
                    </tbody>
                </table>

                <h3 className={styles.sectionTitle} style={s_secTitle}>Column Description</h3>
                <table className={styles.dataTable} style={descTableStyle}>
                    <thead>
                        <tr>
                            <th style={{ width: '25%', ...descThStyle }}>Column</th>
                            <th style={descThStyle}>Description</th>
                        </tr>
                    </thead>
                    <tbody style={{ textAlign: 'left', lineHeight: '1.4' }}>
                        <tr><td style={descColTdStyle}><strong>Total Read</strong></td><td style={descTextTdStyle}>The total number of reads generated from the sequencing process.</td></tr>
                        <tr><td style={descColTdStyle}><strong>Mapped Read</strong></td><td style={descTextTdStyle}>The number of reads that successfully aligned to the reference genome.</td></tr>
                        <tr><td style={descColTdStyle}><strong>Mapping Rate (%)</strong></td><td style={descTextTdStyle}>The ratio of mapped reads to total reads, indicating alignment efficiency.</td></tr>
                        <tr><td style={descColTdStyle}><strong>Expressed gene</strong></td><td style={descTextTdStyle}>The total number of Expressed genes detected in the sample</td></tr>
                        <tr><td style={descColTdStyle}><strong>fusion gene</strong></td><td style={descTextTdStyle}>The total number of fusion genes detected in the sample</td></tr>
                    </tbody>
                </table>

                {renderFooter(pageNum_Mapping)}
            </div>

            {/* --- Page 4: Quality Control (Raw file) - Table --- */}
            <div className={`pdf-page ${styles.a4Page}`} id="sec-qc-raw">
                {renderPageHeader(`${secQc}. Quality Control (Raw file)`)}

                <p className={styles.description} style={{ ...s_desc, marginBottom: '15px' }}>
                    Quality control of raw sequencing data was performed using FastQC (v0.12.1). Adapters and low-quality bases were removed using Cutadapt (v1.18), and sequence data manipulation and summary statistics were processed with SeqKit (v2.13.0).
                </p>

                <h3 className={styles.sectionTitle} style={{ ...s_secTitle, marginTop: '10px', marginBottom: '8px' }}>QC Summary</h3>
                <table className={styles.dataTable} style={s_table}>
                    <thead>
                        <tr>
                            <th>File Name</th>
                            <th>Type</th>
                            <th>Total reads</th>
                            <th>Total base pair</th>
                            <th>Q20(%)</th>
                            <th>Q30(%)</th>
                            <th>GC(%)</th>
                        </tr>
                    </thead>
                    <tbody style={{ textAlign: 'center' }}>
                        {qcDataRaw.length > 0 ? (
                            qcDataRaw.map((qc, idx) => {
                                const { fileName, readType } = getFileInfo(qc.type);
                                return (
                                    <tr key={idx}>
                                        <td style={{ fontWeight: 'bold' }}>{fileName}</td>
                                        <td>{readType}</td>
                                        <td>{qc.totalReads ? Number(qc.totalReads).toLocaleString() : '-'}</td>
                                        <td>{qc.totalBasePair ? Number(qc.totalBasePair).toLocaleString() : '-'}</td>
                                        <td>{qc.q20 || '-'}</td>
                                        <td>{qc.q30 || '-'}</td>
                                        <td>{qc.gc || '-'}</td>
                                    </tr>
                                );
                            })
                        ) : (
                            <tr><td colSpan="7" style={{ padding: '20px', color: '#9ca3af' }}>No QC data available.</td></tr>
                        )}
                    </tbody>
                </table>

                <h3 className={styles.sectionTitle} style={s_secTitle}>Column Description</h3>
                <table className={styles.dataTable} style={descTableStyle}>
                    <thead>
                        <tr>
                            <th style={{ width: '25%', ...descThStyle }}>Column</th>
                            <th style={descThStyle}>Description</th>
                        </tr>
                    </thead>
                    <tbody style={{ textAlign: 'left', lineHeight: '1.4' }}>
                        <tr><td style={descColTdStyle}><strong>File Name</strong></td><td style={descTextTdStyle}>Name of the sequencing file</td></tr>
                        <tr><td style={descColTdStyle}><strong>Type</strong></td><td style={descTextTdStyle}>Information of paired reads (Forward or Reverse)</td></tr>
                        <tr><td style={descColTdStyle}><strong>Total reads</strong></td><td style={descTextTdStyle}>Number of sequences</td></tr>
                        <tr><td style={descColTdStyle}><strong>Total base pair</strong></td><td style={descTextTdStyle}>Total number of bases</td></tr>
                        <tr><td style={descColTdStyle}><strong>Q20(%)</strong></td><td style={descTextTdStyle}>Percentage of bases with quality scores over 20</td></tr>
                        <tr><td style={descColTdStyle}><strong>Q30(%)</strong></td><td style={descTextTdStyle}>Percentage of bases with quality scores over 30</td></tr>
                        <tr><td style={descColTdStyle}><strong>GC(%)</strong></td><td style={descTextTdStyle}>Percentage of GC content</td></tr>
                    </tbody>
                </table>

                {renderFooter(pageNum_QC_Raw)}
            </div>

            {/* --- Page 5: Quality Control (Raw file) - Images --- */}
            <div className={`pdf-page ${styles.a4Page}`}>
                {renderPageHeader(`${secQc}. Quality Control (Raw file)`)}

                <h3 className={styles.sectionTitle} style={{ ...s_secTitle, marginTop: '0', marginBottom: '8px' }}>Per Base Sequence Quality</h3>
                <p className={styles.description} style={s_desc}>
                    The plots below show the quality scores across all bases in the sequencing reads. A higher score indicates better quality. The yellow box represents the inter-quartile range, and the red line represents the median quality.
                </p>

                {/* Read 1 FastQC */}
                <h4 style={s_subTitle}>Read 1</h4>
                <div style={{ width: '100%', height: '450px', backgroundColor: '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', padding: '10px', boxSizing: 'border-box', marginBottom: '15px', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                    {fastqcImagesRaw.read1 ? (
                        <img src={fastqcImagesRaw.read1} alt="Read 1 FastQC" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                    ) : (
                        <span style={{ color: '#9ca3af', fontWeight: 'bold' }}>[Read 1 FastQC Image Not Found]</span>
                    )}
                </div>

                {/* Read 2 FastQC */}
                <h4 style={s_subTitle}>Read 2</h4>
                <div style={{ width: '100%', height: '450px', backgroundColor: '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', padding: '10px', boxSizing: 'border-box', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                    {fastqcImagesRaw.read2 ? (
                        <img src={fastqcImagesRaw.read2} alt="Read 2 FastQC" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                    ) : (
                        <span style={{ color: '#9ca3af', fontWeight: 'bold' }}>[Read 2 FastQC Image Not Found]</span>
                    )}
                </div>

                {renderFooter(pageNum_FastQC_Raw)}
            </div>

            {/* --- Page 6: Quality Control (Trimmed file) - Table --- */}
            <div className={`pdf-page ${styles.a4Page}`}>
                {renderPageHeader(`${secQc}. Quality Control (Trimmed file)`)}

                <p className={styles.description} style={{ ...s_desc, marginBottom: '15px' }}>
                    Quality control of trimmed sequencing data was performed using FastQC (v0.12.1). Adapters and low-quality bases were removed using Cutadapt (v1.18), and sequence data manipulation and summary statistics were processed with SeqKit (v2.13.0).
                </p>

                <h3 className={styles.sectionTitle} style={{ ...s_secTitle, marginTop: '10px', marginBottom: '8px' }}>QC Summary</h3>
                <table className={styles.dataTable} style={s_table}>
                    <thead>
                        <tr>
                            <th>File Name</th>
                            <th>Type</th>
                            <th>Total reads</th>
                            <th>Total base pair</th>
                            <th>Q20(%)</th>
                            <th>Q30(%)</th>
                            <th>GC(%)</th>
                        </tr>
                    </thead>
                    <tbody style={{ textAlign: 'center' }}>
                        {qcDataTrim.length > 0 ? (
                            qcDataTrim.map((qc, idx) => {
                                const { fileName, readType } = getFileInfo(qc.type);
                                return (
                                    <tr key={idx}>
                                        <td style={{ fontWeight: 'bold' }}>{fileName}</td>
                                        <td>{readType}</td>
                                        <td>{qc.totalReads ? Number(qc.totalReads).toLocaleString() : '-'}</td>
                                        <td>{qc.totalBasePair ? Number(qc.totalBasePair).toLocaleString() : '-'}</td>
                                        <td>{qc.q20 || '-'}</td>
                                        <td>{qc.q30 || '-'}</td>
                                        <td>{qc.gc || '-'}</td>
                                    </tr>
                                );
                            })
                        ) : (
                            <tr><td colSpan="7" style={{ padding: '20px', color: '#9ca3af' }}>No QC data available.</td></tr>
                        )}
                    </tbody>
                </table>

                <h3 className={styles.sectionTitle} style={s_secTitle}>Column Description</h3>
                <table className={styles.dataTable} style={descTableStyle}>
                    <thead>
                        <tr>
                            <th style={{ width: '25%', ...descThStyle }}>Column</th>
                            <th style={descThStyle}>Description</th>
                        </tr>
                    </thead>
                    <tbody style={{ textAlign: 'left', lineHeight: '1.4' }}>
                        <tr><td style={descColTdStyle}><strong>File Name</strong></td><td style={descTextTdStyle}>Name of the sequencing file</td></tr>
                        <tr><td style={descColTdStyle}><strong>Type</strong></td><td style={descTextTdStyle}>Information of paired reads (Forward or Reverse)</td></tr>
                        <tr><td style={descColTdStyle}><strong>Total reads</strong></td><td style={descTextTdStyle}>Number of sequences</td></tr>
                        <tr><td style={descColTdStyle}><strong>Total base pair</strong></td><td style={descTextTdStyle}>Total number of bases</td></tr>
                        <tr><td style={descColTdStyle}><strong>Q20(%)</strong></td><td style={descTextTdStyle}>Percentage of bases with quality scores over 20</td></tr>
                        <tr><td style={descColTdStyle}><strong>Q30(%)</strong></td><td style={descTextTdStyle}>Percentage of bases with quality scores over 30</td></tr>
                        <tr><td style={descColTdStyle}><strong>GC(%)</strong></td><td style={descTextTdStyle}>Percentage of GC content</td></tr>
                    </tbody>
                </table>

                {renderFooter(pageNum_QC_Trim)}
            </div>

            {/* --- Page 7: Quality Control (Trimmed file) - Images --- */}
            <div className={`pdf-page ${styles.a4Page}`}>
                {renderPageHeader(`${secQc}. Quality Control (Trimmed file)`)}

                <h3 className={styles.sectionTitle} style={{ ...s_secTitle, marginTop: '0', marginBottom: '8px' }}>Per Base Sequence Quality</h3>
                <p className={styles.description} style={s_desc}>
                    The plots below show the quality scores across all bases in the sequencing reads. A higher score indicates better quality. The yellow box represents the inter-quartile range, and the red line represents the median quality.
                </p>

                {/* Read 1 FastQC */}
                <h4 style={s_subTitle}>Read 1</h4>
                <div style={{ width: '100%', height: '450px', backgroundColor: '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', padding: '10px', boxSizing: 'border-box', marginBottom: '15px', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                    {fastqcImagesTrim.read1 ? (
                        <img src={fastqcImagesTrim.read1} alt="Read 1 Trim FastQC" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                    ) : (
                        <span style={{ color: '#9ca3af', fontWeight: 'bold' }}>[Read 1 Trim FastQC Image Not Found]</span>
                    )}
                </div>

                {/* Read 2 FastQC */}
                <h4 style={s_subTitle}>Read 2</h4>
                <div style={{ width: '100%', height: '450px', backgroundColor: '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', padding: '10px', boxSizing: 'border-box', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                    {fastqcImagesTrim.read2 ? (
                        <img src={fastqcImagesTrim.read2} alt="Read 2 Trim FastQC" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                    ) : (
                        <span style={{ color: '#9ca3af', fontWeight: 'bold' }}>[Read 2 Trim FastQC Image Not Found]</span>
                    )}
                </div>

                {renderFooter(pageNum_FastQC_Trim)}
            </div>

            {/* --- Page 8: Gene Expression --- */}
            <div className={`pdf-page ${styles.a4Page}`} id="sec-gene-exp">
                {renderPageHeader(`${secGeneExp}. Gene Expression`)}

                <h3 className={styles.sectionTitle} style={{ ...s_secTitle, marginTop: '0', marginBottom: '8px' }}>Gene Expression Quantification & Normalization</h3>
                <p className={styles.description} style={s_desc}>
                    Gene-level expression was quantified as transcripts per million (TPM), a measure that accounts for gene length and library size using expect counts from using RSEM (v1.2.28) and log transformed to log2(TPM + 1). Genes with low expression were removed and additional normalization was applied to mitigate potential batch effects. (in-house method using predetermined normalization factors to ensure that already-normalized expression values do not change when new samples are added)
                </p>

                <h3 className={styles.sectionTitle} style={{ ...s_secTitle, marginTop: '10px', marginBottom: '8px' }}>Gene Expression Profile Summary</h3>
                <table className={styles.dataTable} style={{ ...s_table, width: '100%', tableLayout: 'fixed' }}>
                    <thead>
                        <tr>
                            <th style={{ width: '60%' }}>Gene biotype</th>
                            <th style={{ width: '40%' }}>Gene count</th>
                        </tr>
                    </thead>
                    <tbody style={{ textAlign: 'center' }}>
                        {geneExpressionProfile.length > 0 ? (
                            geneExpressionProfile.map((item, idx) => (
                                <tr key={idx}>
                                    <td style={{ fontWeight: 'bold', textAlign: 'left', paddingLeft: '15px' }}>{item.biotype}</td>
                                    <td>{item.count ? Number(item.count).toLocaleString() : '0'}</td>
                                </tr>
                            ))
                        ) : (
                            <tr><td colSpan="2" style={{ padding: '20px', color: '#9ca3af' }}>No gene expression profile data available.</td></tr>
                        )}
                    </tbody>
                </table>

                <ul className={styles.description} style={{ ...s_desc, paddingLeft: '20px', listStyleType: 'disc', marginBottom: '25px', lineHeight: '1.5' }}>
                    <li style={{ marginBottom: '4px' }}>
                        <strong>Protein-coding genes :</strong> protein_coding
                    </li>
                    <li style={{ marginBottom: '4px' }}>
                        <strong>Immune receptor genes :</strong> IG_C_gene, IG_D_gene, IG_J_gene, IG_V_gene, TR_C_gene, TR_D_gene, TR_J_gene, TR_V_gene
                    </li>
                    <li style={{ marginBottom: '4px' }}>
                        <strong>Non-coding RNAs :</strong> lncRNA, miRNA, misc_RNA, ribozyme, scaRNA, scRNA, snoRNA, snRNA, sRNA, vault_RNA
                    </li>
                    <li style={{ marginBottom: '4px' }}>
                        <strong>ribosomal / mitochondrial RNAs :</strong> rRNA, Mt_rRNA, Mt_tRNA
                    </li>
                    <li style={{ marginBottom: '4px' }}>
                        <strong>Pseudogenes :</strong> Includes all gene types annotated as pseudogenes (e.g., processed, unprocessed, polymorphic, etc.)
                    </li>
                    <li>
                        <strong>Putative / unconfirmed :</strong> TEC, artifact
                    </li>
                </ul>

                <h3 className={styles.sectionTitle} style={{ ...s_secTitle, marginTop: '0' }}>
                    Gene Expression Result Table
                    <button
                        onClick={handleDownloadExpression}
                        style={{ background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', fontWeight: 'bold', fontSize: '13px', marginLeft: '10px' }}
                    >
                        (<span style={{ textDecoration: 'underline' }}>Download</span>)
                    </button>
                </h3>
                <table className={styles.dataTable} style={{ ...descTableStyle, fontSize: '9px', marginBottom: '0' }}>
                    <thead>
                        <tr>
                            <th style={{ width: '22%', ...descThStyle }}>Column</th>
                            <th style={descThStyle}>Description</th>
                        </tr>
                    </thead>
                    <tbody style={{ textAlign: 'left', lineHeight: '1.4' }}>
                        <tr>
                            <td style={descColTdStyle}><strong>ensembl</strong></td>
                            <td style={descTextTdStyle}>Unique gene identifier for Ensembl database</td>
                        </tr>
                        <tr>
                            <td style={descColTdStyle}><strong>geneSymbol</strong></td>
                            <td style={descTextTdStyle}>Gene symbol from HGNC</td>
                        </tr>
                        <tr>
                            <td style={descColTdStyle}><strong>geneDescription</strong></td>
                            <td style={descTextTdStyle}>Functional annotation or protein description of a gene</td>
                        </tr>
                        <tr>
                            <td style={descColTdStyle}><strong>geneType</strong></td>
                            <td style={descTextTdStyle}>Gene classification (i.e. protein_coding) from Ensembl database (Biotypes)</td>
                        </tr>
                        <tr>
                            <td style={descColTdStyle}><strong>GBCC sample code</strong></td>
                            <td style={descTextTdStyle}>Gene expression value for a sample (one column per sample). Values are log2(TPM + 1) after in-house low-signal filtering, normalization using pre determined normalization factors derived from a fixed reference distribution, and empirical batch correction.</td>
                        </tr>
                    </tbody>
                </table>

                {renderFooter(pageNum_GeneExp)}
            </div>

            {/* --- Page 9: Gene Fusion --- */}
            <div className={`pdf-page ${styles.a4Page}`} id="sec-fusion">
                {renderPageHeader(`${secFusion}. Gene Fusion`)}

                <h3 style={{ ...s_paraTitle, marginBottom: '4px' }}>Fusion analysis</h3>
                <p className={styles.description} style={{ ...s_desc, marginBottom: '12px' }}>
                    Arriba (v2.4.0) was used to detect gene fusions and structural rearrangements from RNA-seq data. It identifies high-confidence fusion candidates by analyzing chimeric alignments and discordant read pairs.
                </p>

                <h3 className={styles.sectionTitle} style={{ ...s_secTitle, marginTop: '10px', marginBottom: '8px' }}>Fusion Summary</h3>
                <table className={styles.dataTable} style={{ ...s_table, marginBottom: '15px' }}>
                    <thead>
                        <tr>
                            <th style={{ width: '30%' }}>Sample Name</th>
                            <th style={{ width: '30%' }}>Fusion gene count</th>
                            <th style={{ width: '40%' }}>Target interest - gene</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td style={{ fontWeight: 'bold' }}>{data.sampleName}</td>
                            <td>{data.mappingSummary?.fusionGene ? Number(data.mappingSummary.fusionGene).toLocaleString() : '-'}</td>
                            <td style={{ color: data.targetFusionGenes ? '#ef4444' : '#4b5563', fontWeight: data.targetFusionGenes ? 'bold' : 'normal' }}>
                                {data.targetFusionGenes ? data.targetFusionGenes : '-'}
                            </td>
                        </tr>
                    </tbody>
                </table>

                <h3 className={styles.sectionTitle} style={{ ...s_secTitle, marginTop: '0' }}>
                    Fusion Result Table
                    <button
                        onClick={handleDownloadFusion}
                        style={{ background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', fontWeight: 'bold', fontSize: '13px', marginLeft: '10px' }}
                    >
                        (<span style={{ textDecoration: 'underline' }}>Download</span>)
                    </button>
                </h3>

                <table className={styles.dataTable} style={{ ...descTableStyle, fontSize: '9px', marginBottom: '0' }}>
                    <thead>
                        <tr>
                            <th style={{ width: '22%', ...descThStyle }}>Feature</th>
                            <th style={descThStyle}>Description</th>
                        </tr>
                    </thead>
                    <tbody style={{ lineHeight: '1.2' }}>
                        {[
                            { col: 'Tumor Sample Barcode v2', desc: 'Unique NGS identification code for the sample.' },
                            { col: 'Fusion Gene 1 & 2', desc: 'The two specific genes involved in the fusion event.' },
                            { col: 'Gene Site 1 & 2', desc: 'The specific genomic locations (breakpoints) where the fusion occurred for each gene.' },
                            { col: 'Structural Variation', desc: 'The type of structural rearrangement (e.g., translocation, inversion, deletion).' },
                            { col: 'Prediction Confidence', desc: "The reliability level of the detected fusion. While results are pre-filtered by the pipeline, column 'W' allows for additional filtering. All results are typically sent to external partners regardless of this value." },
                            { col: 'Reading Frame', desc: 'Information on whether the fusion results in an In-frame or Out-of-frame transcript.' },
                            { col: 'Fused Protein Domains', desc: 'Details regarding the functional protein domains preserved in the resulting fusion.' },
                            { col: 'Ensembl ID 1 & 2', desc: 'Unique Ensembl identifiers for each involved gene.' },
                            { col: 'Fused Direction 1 & 2', desc: "The orientation (5' or 3') and strand direction of each gene in the fusion." },
                            { col: 'Genome Align Status', desc: 'The status of the alignment of the fusion sequence back to the reference genome.' },
                            { col: 'Fusion Transcript', desc: 'The predicted nucleotide sequence of the resulting fusion transcript.' },
                            { col: 'Peptide Sequence', desc: 'The predicted amino acid sequence of the resulting fusion protein.' },
                            { col: 'Target Interest - Fused', desc: 'Indicates ("YES") if a fusion involves high-interest targets: ALK, ROS1, RET, AXL, or TPX2.' },
                            { col: 'Target', desc: "Specifically outputs the name of the target gene identified in the 'Target Interest' field." }
                        ].map((item, idx) => (
                            <tr key={idx}>
                                <td style={descColTdStyle}><strong>{item.col}</strong></td>
                                <td style={descTextTdStyle}>{item.desc}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                {renderFooter(pageNum_Fusion)}
            </div>

            {/* ★ 동적으로 퓨전 다이어그램 페이지 생성 */}
            {chunkedImages.map((imageChunk, pageIndex) => {
                const pageNum_Diagram = currentPage + pageIndex;

                return (
                    <div key={`fusion-pdf-page-${pageIndex}`} className={`pdf-page ${styles.a4Page}`} id={pageIndex === 0 ? 'sec-diagram' : undefined}>

                        {renderPageHeader(`${secDiagram}. Gene Fusion Diagrams (Page ${pageIndex + 1})`)}

                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '20px',
                            margin: '15px 0'
                        }}>
                            {imageChunk.map((imgBase64, imgIdx) => (
                                <div key={`fusion-img-${imgIdx}`} style={{
                                    height: '400px',
                                    width: '100%',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    border: '1px solid #e5e7eb',
                                    backgroundColor: '#f9fafb',
                                    borderRadius: '8px',
                                    overflow: 'hidden',
                                    padding: '10px',
                                    boxSizing: 'border-box'
                                }}>
                                    <img
                                        src={imgBase64}
                                        alt={`Fusion Structure ${pageIndex * 2 + imgIdx + 1}`}
                                        style={{
                                            maxWidth: '100%',
                                            maxHeight: '100%',
                                            objectFit: 'contain'
                                        }}
                                    />
                                </div>
                            ))}
                        </div>

                        {renderFooter(pageNum_Diagram)}
                    </div>
                );
            })}

        </div> // end of PDF
    );
}

export default RNAInnerReportComponent;